import useSWR from 'swr';
import fetchDataService from '../services/fetchDataService';

/**
 * Hooks SWR del módulo WPBOT.
 *
 * Todos los endpoints del backend (:5006) son POST bajo /wpbot/* con el
 * envelope cifrado del ERP. Cada request lleva `company` (uid de la compañía
 * en sesión); el companyMiddleware del backend valida la pertenencia e
 * inyecta el company_id numérico en servidor. Cada request lleva además la
 * `session` (cookie de Firebase devuelta en create-authsession), que el
 * backend exige en verify_session al no poder leer cookies cross-origin.
 */

const withAuth = (tokenData: any, requestData: any) => ({
  ...requestData,
  csrfToken: tokenData?.token?.uid,
  session: tokenData?.session,
});

export function useBots(tokenData:any, requestData:any){
  const {company} = requestData;
  const url = '/wpbot/list-bots';
  const fetcher = () => fetchDataService.post(url, tokenData.key, withAuth(tokenData, requestData));
  const {data, error, isLoading, mutate} = useSWR(
    ()=>(tokenData?.key && company) ? [url, company, tokenData.token.uid] : null, 
    fetcher, 
    {keepPreviousData:true}
  );
  return { data, error, isLoading, mutate }
}

export function useBot(tokenData:any, requestData:any){
  const {id, company} = requestData;
  const url = '/wpbot/get-bot';
  const fetcher = () => fetchDataService.post(url, tokenData.key, withAuth(tokenData, requestData));
  const {data, error, isLoading, mutate} = useSWR(
    ()=>(tokenData?.key && id && company) ? [`${url}/${id}`, company, tokenData.token.uid] : null, 
    fetcher
  );
  return { data, error, isLoading, mutate }
}

export function useBotTools(tokenData:any, requestData:any){
  const {bot_id, company} = requestData;
  const url = '/wpbot/list-tools';
  const fetcher = () => fetchDataService.post(url, tokenData.key, withAuth(tokenData, requestData));
  const {data, error, isLoading, mutate} = useSWR(
    ()=>(tokenData?.key && bot_id && company) ? [url, bot_id, tokenData.token.uid] : null, 
    fetcher, 
    {keepPreviousData:true}
  );
  return { data, error, isLoading, mutate }
}

export function useBotContacts(tokenData:any, requestData:any){
  const {bot_id, company} = requestData;
  const url = '/wpbot/list-bot-contacts';
  const fetcher = () => fetchDataService.post(url, tokenData.key, withAuth(tokenData, requestData));
  const {data, error, isLoading, mutate} = useSWR(
    ()=>(tokenData?.key && bot_id && company) ? [url, bot_id, tokenData.token.uid] : null, 
    fetcher, 
    {keepPreviousData:true}
  );
  return { data, error, isLoading, mutate }
}

export function useConversations(tokenData:any, requestData:any){
  const {bot_id, company} = requestData;
  const url = '/wpbot/list-conversations';
  const fetcher = () => fetchDataService.post(url, tokenData.key, withAuth(tokenData, requestData));
  const {data, error, isLoading, mutate} = useSWR(
    ()=>(tokenData?.key && bot_id && company) ? [url, bot_id, tokenData.token.uid] : null, 
    fetcher, 
    {keepPreviousData:true, refreshInterval: 4000}
  );
  return { data, error, isLoading, mutate }
}

export function useConversationMessages(tokenData:any, requestData:any){
  const {conversation_id, company} = requestData;
  const url = '/wpbot/get-conversation-messages';
  const fetcher = () => fetchDataService.post(url, tokenData.key, withAuth(tokenData, requestData));
  const {data, error, isLoading, mutate} = useSWR(
    ()=>(tokenData?.key && conversation_id && company) ? [`${url}/${conversation_id}`, company, tokenData.token.uid] : null, 
    fetcher,
    { refreshInterval: 4000 }
  );
  return { data, error, isLoading, mutate }
}

export function useBotApps(tokenData:any, requestData:any){
  const {bot_id, company} = requestData;
  const url = '/wpbot/list-bot-apps';
  const fetcher = () => fetchDataService.post(url, tokenData.key, withAuth(tokenData, requestData));
  const {data, error, isLoading, mutate} = useSWR(
    ()=>(tokenData?.key && bot_id && company) ? [url, bot_id, tokenData.token.uid] : null, 
    fetcher, 
    {keepPreviousData:true}
  );
  return { data, error, isLoading, mutate }
}

export function useCalendars(tokenData:any, requestData:any){
  const {company} = requestData;
  const url = '/wpbot/get-bulk-calendars';
  const fetcher = () => fetchDataService.post(url, tokenData.key, withAuth(tokenData, requestData));
  const {data, error, isLoading, mutate} = useSWR(
    ()=>(tokenData?.key && company) ? [url, company, tokenData.token.uid] : null, 
    fetcher
  );
  return { data, error, isLoading, mutate }
}
